import { use, useEffect } from "react";
import { buildApiUrl } from "../../services/apiConfig";
import axios from "axios";

class DashboardService {
  constructor() {
    this.API_HOST = process.env.REACT_APP_API_URL;
    this.storedUserId = sessionStorage.getItem('user_id');
    this._isCreatingDefaultTab = false;
  }

  async fetchClients(userId) {
    try {
      const response = await fetch(buildApiUrl('AUTH', `/get-client-info/all?user_id=${userId}`));
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching clients:', error);
      throw error;
    }
  }

  async fetchWidgetsAndTabs(selectedUseCase, userId) {
    if (this._isCreatingDefaultTab) {
      return { success: true, tabs: [], isProcessing: true };
    }

    try {
      const response = await fetch(buildApiUrl('AUTH', `/api/widgets/by-tabs?use_case_id=${selectedUseCase}&user_id=${userId}`));
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      const data = await response.json();

      if (!data.tabs || data.tabs.length === 0) {
        return await this.createDefaultTabWithWidgets(selectedUseCase, userId);
      }

      return data;
    } catch (error) {
      this._isCreatingDefaultTab = false;
      console.error('Error fetching widgets and tabs:', error);
      throw error;
    }
  }

  /**
 * Fetches default widgets for a use case
 * @param {string} useCaseId - The use case ID
 * @returns {Promise<Object>} Default widgets data
 */
  async fetchDefaultWidgets(useCaseId) {
    try {
      const response = await fetch(buildApiUrl('AUTH', `/api/widgets?use_case_id=${useCaseId}&user_id=default`));
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching default widgets:", error);
      throw error;
    }
  }

  /**
   * Creates a default "Overview" tab with default widgets
   * @param {string} useCaseId - The use case ID
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} Updated object containing tabs and widgets
   */
  async createDefaultTabWithWidgets(useCaseId, userId) {
    if (this._isCreatingDefaultTab) {
      return { success: true, tabs: [], isProcessing: true };
    }

    this._isCreatingDefaultTab = true;

    try {
      try {
        const existingTabsResponse = await fetch(buildApiUrl('AUTH', `/api/tabs?use_case_id=${useCaseId}`));

        if (existingTabsResponse.ok) {
          const existingTabsData = await existingTabsResponse.json();
          if (existingTabsData.tabs && existingTabsData.tabs.length > 0) {
            this._isCreatingDefaultTab = false;
            return { success: true, tabs: existingTabsData.tabs };
          }
        } else if (existingTabsResponse.status === 404) {
        }
      } catch (err) {
      }

      const defaultWidgetsResponse = await this.fetchDefaultWidgets(useCaseId);
      if (!defaultWidgetsResponse.success) {
        throw new Error("Failed to fetch default widgets");
      }

      const defaultWidgets = defaultWidgetsResponse.widgets || [];

      const tabId = `tab_${Date.now().toString().slice(-5)}`;
      const tabName = "Overview";

      const tabResponse = await this.createTab(tabId, tabName, useCaseId);

      if (!tabResponse || !tabResponse.message || !tabResponse.message.includes('successfully')) {
        throw new Error("Failed to create default tab");
      }

      const widgetPromises = defaultWidgets.map(widget => {
        const widgetId = `${Date.now().toString().slice(-2)}${Math.floor(Math.random() * 1000)}`;

        return this.createWidget({
          id: widgetId,
          name: widget.widget_name,
          tool_id: widget.tool_id,
          tool_prompt: widget.prompt || "",
          visual_prompt: widget.visual_prompt || "",
          tab_id: tabId,
          use_case_id: useCaseId,
          user_id: userId
        });
      });

      try {
        await Promise.all(widgetPromises);
      } catch (widgetError) {
        console.error("Error creating widgets:", widgetError);
      }

      const result = {
        success: true,
        tabs: [{
          tab_id: tabId,
          tab_name: tabName,
          use_case_id: useCaseId,
          widgets: defaultWidgets.map((widget, index) => ({
            widget_id: `${Date.now().toString().slice(-5)}_${index}`,
            widget_name: widget.widget_name || "Default Widget",
            subtitle: widget.subtitle || "",
            prompt: widget.prompt || "",
            visual_prompt: widget.visual_prompt || "",
            tool_name: widget.tool_name || "SQL Query Assistant",
            endpoint: widget.endpoint || ":5009/sql-ask",
            tabId: tabId
          }))
        }]
      };

      this._isCreatingDefaultTab = false;
      return result;
    } catch (error) {
      this._isCreatingDefaultTab = false;
      console.error("Error creating default tab with widgets:", error);
      return {
        success: false,
        tabs: [{
          tab_id: `tab_${Date.now().toString().slice(-5)}`,
          tab_name: "Overview",
          use_case_id: useCaseId,
          widgets: []
        }],
        error: error.message,
        fallback: true 
      };
    }
  }

  async fetchUseCases() {
    try {
      const response = await fetch(buildApiUrl('AUTH', '/fetch/use_cases'));
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching use cases:', error);
      throw error;
    }
  }

  async fetchTools() {
    try {
      const response = await fetch(buildApiUrl('AUTH', '/fetch/agents'));
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching tools:', error);
      throw error;
    }
  }

  async createTab(tabId, tabName, useCaseId) {
    try {
      const body = {
        id: tabId,
        name: tabName,
        use_case_id: useCaseId
      };
      const response = await fetch(buildApiUrl('AUTH', '/table-backend/tabs'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error("Failed to create new tab");
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating tab:', error);
      throw error;
    }
  }

  async deleteTab(tabId) {
    try {
      const body = { where: `id = '${tabId}'` };
      const response = await fetch(buildApiUrl('AUTH', '/table-backend/tabs'), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error("Failed to delete tab");
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting tab:', error);
      throw error;
    }
  }

  async updateTabName(tabId, tabName) {
    try {
      const response = await fetch(buildApiUrl('SQL', '/run-update-backend'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `UPDATE tabs SET name = '${tabName.replace(/'/g, "''")}' WHERE id = '${tabId}';`
        })
      });
      if (!response.ok) {
        throw new Error("Failed to update tab name");
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating tab name:', error);
      throw error;
    }
  }

  async createWidget(widgetData) {
    try {
      const response = await fetch(buildApiUrl('AUTH', '/table-backend/widgets'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(widgetData)
      });
      if (!response.ok) {
        throw new Error("Failed to create widget");
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating widget:', error);
      throw error;
    }
  }

  async deleteWidget(widgetId) {
    try {
      const body = { where: `id = '${widgetId}'` };
      const response = await fetch(buildApiUrl('AUTH', '/table-backend/widgets'), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        throw new Error("Failed to delete widget");
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting widget:', error);
      throw error;
    }
  }

  async updateWidget(widgetId, updateData) {
    try {
      const escapedPrompt = (updateData.tool_prompt || '').replace(/'/g, "''");
      const escapedChartPrompt = (updateData.visual_prompt || '').replace(/'/g, "''");
      const escapedTitle = (updateData.name || '').replace(/'/g, "''");

      let updateQuery = `UPDATE widgets SET `;
      const updateParts = [];

      if (updateData.tool_prompt !== undefined) {
        updateParts.push(`tool_prompt = '${escapedPrompt}'`);
      }

      if (updateData.visual_prompt !== undefined) {
        updateParts.push(`visual_prompt = '${escapedChartPrompt}'`);
      }

      if (updateData.name !== undefined) {
        updateParts.push(`name = '${escapedTitle}'`);
      }

      if (updateParts.length === 0) {
        return { success: true, message: "No changes to update" };
      }

      updateQuery += updateParts.join(', ');
      updateQuery += ` WHERE id = '${widgetId}';`;

      const response = await fetch(buildApiUrl('SQL', '/run-update-backend'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: updateQuery })
      });

      if (!response.ok) {
        throw new Error("Failed to update widget in the database");
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating widget:', error);
      throw error;
    }
  }

  async exportToPPT(slidesData) {
    try {
      const response = await fetch(buildApiUrl('EXPORT', '/export-native-ppt-with-charts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slidesData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API responded with status: ${response.status}. Details: ${errorText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error exporting to PPT:', error);
      throw error;
    }
  }

  async generateWidgetContent(tool, prompt, customerId, userId, useCaseId, agent_id, context = null, collection = 'default_collection') {
    try {
      let response;

      switch (tool) {
        case 'SQL Query Assistant':
          const formData = new FormData();
          formData.append('question', prompt);
          formData.append('cust_id', customerId);
          formData.append('advisor_id', userId);
          formData.append('use_case_id', useCaseId);
          formData.append('agent_id', agent_id);

          response = await fetch(buildApiUrl('SQL_AGENT', '/sql-ask'), {
            method: 'POST',
            body: formData
          });
          break;

        case 'Transcript Analysis':
          if (!context) {
            return { success: false, error: 'No context provided for analysis' };
          }

          const transcriptionFile = new Blob([context], { type: 'text/plain' });
          const transcriptFormData = new FormData();
          transcriptFormData.append('file', transcriptionFile, 'transcription.txt');
          transcriptFormData.append('prompt', prompt);

          response = await fetch(buildApiUrl('JAMBA', '/custom-analyze'), {
            method: 'POST',
            body: transcriptFormData
          });
          break;

        case 'Retrieval Tool':
          const ragFormData = new FormData();
          ragFormData.append('query', prompt);
          ragFormData.append('cust_id', customerId);
          ragFormData.append('advisor_id', this.storedUserId);
          ragFormData.append('use_case_id', useCaseId);
          ragFormData.append('agent_id', agent_id);
          ragFormData.append('collection', collection);

          response = await fetch(buildApiUrl('RAG', '/rag_base'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: ragFormData
          });
          break;

        case 'Retail Tool':
          response = await fetch(buildApiUrl('RETAIL', '/query'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: prompt })
          });
          break;

        case 'Transcript Insight Engine':
          if (!context) {
            return { success: false, error: 'No context provided for analysis' };
          }

          const insightFormData = new FormData();
          insightFormData.append('query', `having this conversation as context: ${context} ; ${prompt}`);
          insightFormData.append('use_case_id', useCaseId);
          insightFormData.append('agent_id', agent_id);
          insightFormData.append('cust_id', customerId);
          insightFormData.append('advisor_id', this.storedUserId);
          insightFormData.append('collection', collection);

          response = await fetch(buildApiUrl('RAG', '/rag_base'), {
            method: 'POST',
            body: insightFormData
          });
          break;

        case 'NOVA':
          const novaFormData = new FormData();
          novaFormData.append('query', prompt);
          novaFormData.append('cust_id', customerId);
          novaFormData.append('advisor_id', userId);
          novaFormData.append('use_case_id', useCaseId);
          novaFormData.append('agent_id', agent_id);

          response = await fetch(buildApiUrl('NOVA', '/Nova_chat_stream'), {
            method: 'POST',
            body: novaFormData
          });
          break;

        default:
          return { success: false, error: `Unsupported tool: ${tool}` };
      }

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      // For streaming responses (like NOVA), we need to handle differently
      if (tool === 'NOVA') {
        return { success: true, stream: response.body };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error(`Error generating content with ${tool}:`, error);
      return { success: false, error: error.message };
    }
  }

  async generateChartFromData(data, chartPrompt) {
    try {
      const cssColor = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim();
      const finalColor = cssColor.startsWith('#') ? cssColor : `#${cssColor}`;

      const response = await fetch(buildApiUrl('CHART', '/api/embed-new'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: typeof data === 'string' ? data : JSON.stringify(data),
          query: chartPrompt,
          primary_color: finalColor,
        })
      });

      if (!response.ok) {
        throw new Error(`Chart API responded with status: ${response.status}`);
      }

      const html = await response.text();
      return { success: true, html };
    } catch (error) {
      console.error('Error generating chart:', error);
      return { success: false, error: error.message };
    }
  }

  async getAgentGraph() {
    try {
      const response = await fetch(buildApiUrl('NOVA', `/agent_graph/run?advisor_id=${this.storedUserId}`));

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow graph: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching agent graph:", error);
      throw error;
    }
  }

  async generateTextToSpeech(text) {
    try {
      const response = await fetch(buildApiUrl('TTS', '/tts_normalize'), {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: { text }, summ_flag: true })
      });

      if (!response.ok) {
        throw new Error(`TTS API responded with status: ${response.status}`);
      }

      return { success: true, audioBlob: await response.blob() };
    } catch (error) {
      console.error('Error generating text-to-speech:', error);
      return { success: false, error: error.message };
    }
  }

  async processAudio(audioBlob) {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.wav');

      const response = await fetch(buildApiUrl('AUDIO', '/process_audio'), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Audio processing API responded with status: ${response.status}`);
      }

      return { success: true, data: await response.json() };
    } catch (error) {
      console.error('Error processing audio:', error);
      return { success: false, error: error.message };
    }
  }

  /**
  * Generates a summary of client data based on widget contents using NOVA
  * @param {string} formattedContent - The formatted content from widgets
  * @param {string} customerName - The name of the customer
  * @param {string} userPrompt - Custom prompt for summary generation
  * @returns {Promise<Object>} - The generated summary
  */
  async generateClientSummary(formattedContent, customerId, userPrompt, useCaseId, clientData, isIBRole = false) {
    try {

      // Generate a thread ID or retrieve existing one
      let threadID = sessionStorage.getItem("wealth_thread_id");
      if (!threadID) {
        threadID = this.generateUUID();
        sessionStorage.setItem("wealth_thread_id", threadID);
      }

      const ibFallbackPrompt = "Generate a concise brief on this financial institution as a financial institution client (not a retail investor). Include one short sentence overview and a 2-3 sentence analysis covering: current wallet share, KYC status, active deal pipeline, recent engagement level, and key opportunities or risks for the upcoming coverage call. Do not mention risk tolerance or investment horizon.";
      const retailFallbackPrompt = "Provide me a long and short summary about this portfolio in this form:";

      const prompt = `Having this in context: ${JSON.stringify(clientData)}, ${formattedContent}

    ${userPrompt || (isIBRole ? ibFallbackPrompt : retailFallbackPrompt)}

    Respond ONLY with a JSON object in this exact format:
    {"shortSummary": "brief one-sentence overview", "longSummary": "detailed analysis limited to 70 words maximum"}

    Important instructions:
    - Do not use any formatting (no bold, no italics, no bullet points)
    - Use simple plain text only
    - The longSummary must be 200 words maximum
    - Return ONLY the JSON object with no additional text`;

      const storedUserId = sessionStorage.getItem('user_id') || this.storedUserId;
      const formData = new FormData();
      formData.append("query", prompt);
      formData.append("cust_id", customerId);
      formData.append("advisor_id", storedUserId);
      formData.append("use_case_id", useCaseId);
      formData.append("agent_id", "63493");
      formData.append("thread_id", threadID);

      // Add timeout to prevent hanging (30 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const response = await fetch(buildApiUrl('NOVA', '/Nova_chat_stream'), {
          method: "POST",
          body: formData,
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`API responded with status ${response.status}`);
        }

        const result = await this.processStreamingResponse(response);

        // Try to extract JSON from the response if it's a string
        let summaryData;
        if (typeof result === 'string') {
          try {
            // Look for JSON object in the string
            const jsonMatch = result.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              summaryData = JSON.parse(jsonMatch[0]);
            } else {
              // If no JSON found, create a default structure
              summaryData = {
                shortSummary: result.split('.')[0] + '.',  // First sentence as short summary
                longSummary: result  // Full text as long summary
              };
            }
          } catch (e) {
            console.error('Error parsing NOVA response:', e);
            summaryData = {
              shortSummary: "Summary unavailable",
              longSummary: "Unable to generate summary from the available data."
            };
          }
        } else if (typeof result === 'object') {
          summaryData = result;
        }
        return {
          success: true,
          data: summaryData
        };
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('[API] Exception:', error);
      console.error('Error generating client summary:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Process streaming response from NOVA
   * @param {Response} response - The streaming response from NOVA
   * @returns {Promise<Object|string>} - The processed response
   */
  async processStreamingResponse(response) {
    let accumulatedText = '';

    try {
      const { createParser } = await import('eventsource-parser');

      const parser = createParser({
        onEvent: (event) => {
          const type = event.event;
          if (type === "filler" || type === "interrupt" || type === "final") {
            try {
              const parsedData = JSON.parse(event.data);

              if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                throw new Error(parsedData.ERROR);
              }

              if (parsedData.data.response) {
                accumulatedText += parsedData.data.response;
              } else if (parsedData.data) {
                accumulatedText += parsedData.data;
              }
            } catch (error) {
              // Silently handle parsing errors for individual events
            }
          }
        }
      });

      const reader = response.body.getReader();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        parser.feed(chunk);
      }

      try {
        return JSON.parse(accumulatedText);
      } catch (parseErr) {
        const jsonMatch = accumulatedText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            return JSON.parse(jsonMatch[0]);
          } catch (e) {
            return accumulatedText;
          }
        } else {
          return accumulatedText;
        }
      }
    } catch (err) {
      throw new Error("Failed to process response");
    }
  }

  /**
   * Generate a UUID v4
   * @returns {string} - A UUID v4 string
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0,
        v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Gets the socket.io connection URL for transcription
   * @returns {string} - Socket.io connection URL
   */
  getTranscriptionSocketUrl() {
    return buildApiUrl('SOCKET');
  }

  /**
   * Uploads transcription text to the RAG service
   * @param {string} text - The transcription text to upload
   * @param {string} collectionName - The collection name for the document
   * @returns {Promise<Object>} - Response from the upload API
   */
  async uploadTranscription(text, collectionName = 'U00003') {
    if (!text.trim()) {
      return { success: false, message: "No text to upload" };
    }

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `transcription_${timestamp}.txt`;
      const file = new File([text], fileName, { type: 'text/plain' });

      const formData = new FormData();
      formData.append('files', file);
      formData.append('collection_name', collectionName);

      const response = await axios.post(buildApiUrl('UPLOAD', '/upload-documents'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return {
        success: true,
        data: response.data,
        message: "Transcription uploaded successfully"
      };
    } catch (error) {
      console.error("Error uploading transcription:", error);

      let errorDetails = "";
      if (error.response) {
        errorDetails = `Server responded with status ${error.response.status}`;
        console.error("Server response:", error.response.data);
      } else if (error.request) {
        errorDetails = "No response received from server";
        console.error("No response received:", error.request);
      } else {
        errorDetails = error.message;
        console.error("Error setting up request:", error.message);
      }

      return {
        success: false,
        error: errorDetails,
        message: "Failed to upload transcription"
      };
    }
  }

  async uploadRagDocument(file, collectionName) {
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('collection_name', collectionName);

      const response = await axios.post(buildApiUrl('UPLOAD', '/upload-documents'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error uploading RAG document:', error);
      return { success: false, error: error.message };
    }
  }

  async getSummarizedAudit(runId) {
    try {
      const sumAuditUrl = buildApiUrl('SUM_AUDIT', `/${runId}/summary`);

      const response = await fetch(sumAuditUrl, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error (summarize_audit):`, error);
      throw error;
    }
  };

  /**
    * Downsamples an audio buffer to a target sample rate
    * @param {Float32Array} buffer - The original audio buffer
    * @param {number} sampleRate - The original sample rate
    * @param {number} targetSampleRate - The target sample rate
    * @returns {Float32Array} - The downsampled buffer
    */
  downsampleBuffer(buffer, sampleRate, targetSampleRate) {
    if (targetSampleRate === sampleRate) return buffer;
    if (targetSampleRate > sampleRate) {
      console.error("Target sample rate must be lower than original sample rate");
      return buffer;
    }

    const sampleRateRatio = sampleRate / targetSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < newLength) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0;
      let count = 0;

      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }

      result[offsetResult] = count ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  /**
   * Converts a Float32Array to 16-bit PCM
   * @param {Float32Array} input - The float audio data
   * @returns {ArrayBuffer} - The 16-bit PCM buffer
   */
  floatTo16BitPCM(input) {
    const output = new Int16Array(input.length);

    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    return output.buffer;
  }
}


export const scheduleMeeting = async (meetingData) => {
  try {
    const response = await fetch(buildApiUrl('AUTH', '/table/meetings'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(meetingData)
    });

    if (!response.ok) {
      throw new Error(`Failed to schedule meeting: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error scheduling meeting:', error);
    throw error;
  }


};



export default new DashboardService();